import { CoOwner } from '@/shared/lib/sj-orm';

import transferIndexOptions from './transferIndexOptions.json';
import { CommonAssetDto, TransferIndexFieldGroup } from './types';

export const getAssetTransferIndex = (asset: CommonAssetDto, round?: boolean): number => {
  try {
    const value = calculateTransferIndex(asset);
    return round ? Math.round(value) : value;
  } catch (er) {
    return 0;
  }
};

type GroupStats = Record<TransferIndexFieldGroup, { filled: number; total: number }>;
type TransferIndexCoOwnerMap = Record<string, TransferIndexFieldGroup>;
type TransferIndexOption = {
  type: string;
  map: Record<string, unknown>;
};
type TransferIndexMultiKeyHash = Record<string, string>;

const MULTI_KEY_SEPARATOR = '/';
const MULTI_KEY_HASH_SEPARATOR = '-';
const transferIndexIndexes = transferIndexOptions.indexes as unknown as TransferIndexOption[];
const addressImportantFields = ['country', 'city', 'street', 'buildingNumber'] as const;
const addressTransferIndexFields = new Set(['ownerAddress', 'address']);

const groupsWeight: Record<TransferIndexFieldGroup, number> = {
  [TransferIndexFieldGroup.CRUCIAL]: 70,
  [TransferIndexFieldGroup.ESSENTIAL]: 20,
  [TransferIndexFieldGroup.NECESSARY]: 5,
  [TransferIndexFieldGroup.OTHER]: 5,
};

const availableGroups = Object.values(TransferIndexFieldGroup).filter(
  (value): value is TransferIndexFieldGroup => typeof value === 'number',
);

const splitMultiFieldName = (fieldName: string): string[] =>
  fieldName
    .split(MULTI_KEY_SEPARATOR)
    .map((item) => item.trim())
    .filter(Boolean);

const createTransferIndexMultiKeyHash = (): TransferIndexMultiKeyHash => {
  const hash: TransferIndexMultiKeyHash = {};

  for (const option of transferIndexIndexes) {
    for (const fieldName of Object.keys(option.map)) {
      if (!fieldName.includes(MULTI_KEY_SEPARATOR)) {
        continue;
      }

      for (const singleFieldName of splitMultiFieldName(fieldName)) {
        hash[`${option.type}${MULTI_KEY_HASH_SEPARATOR}${singleFieldName}`] = fieldName;
      }
    }
  }

  return hash;
};

export const transferIndexMultiKeyHash = Object.freeze(createTransferIndexMultiKeyHash());

const createGroupStats = (): GroupStats => ({
  [TransferIndexFieldGroup.CRUCIAL]: { filled: 0, total: 0 },
  [TransferIndexFieldGroup.ESSENTIAL]: { filled: 0, total: 0 },
  [TransferIndexFieldGroup.NECESSARY]: { filled: 0, total: 0 },
  [TransferIndexFieldGroup.OTHER]: { filled: 0, total: 0 },
});

const isTransferIndexGroup = (value: unknown): value is TransferIndexFieldGroup =>
  typeof value === 'number' && availableGroups.includes(value);

const isCoOwnersMap = (value: unknown): value is TransferIndexCoOwnerMap => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  return Object.values(value).every(isTransferIndexGroup);
};

const updateGroupStats = (
  groupStats: GroupStats,
  group: TransferIndexFieldGroup,
  fieldName: string,
  value: unknown,
) => {
  groupStats[group].total += 1;
  if (isFieldFilled(fieldName, value)) {
    groupStats[group].filled += 1;
  }
};

const getTransferIndexFieldValue = (asset: CommonAssetDto, fieldName: string): unknown => {
  if (!fieldName.includes(MULTI_KEY_SEPARATOR)) {
    return asset[fieldName];
  }

  const values = splitMultiFieldName(fieldName).map((singleFieldName) => asset[singleFieldName]);
  return values.find((value) => isNotEmptyValue(value));
};

const isAddressValueFilled = (value: unknown): boolean => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  const addressValue = value as Partial<Record<(typeof addressImportantFields)[number], unknown>>;
  return addressImportantFields.every((field) => isNotEmptyValue(addressValue[field]));
};

const isFieldFilled = (fieldName: string, value: unknown): boolean => {
  if (addressTransferIndexFields.has(fieldName)) {
    return isAddressValueFilled(value);
  }

  return isNotEmptyValue(value);
};

const calculateTransferIndex = (asset: CommonAssetDto): number => {
  const options = transferIndexIndexes.find((item) => item.type === asset.category);

  if (!options) {
    return 0;
  }

  const groupStats = createGroupStats();

  for (const [fieldName, fieldConfig] of Object.entries(options.map)) {
    if (isTransferIndexGroup(fieldConfig)) {
      updateGroupStats(
        groupStats,
        fieldConfig,
        fieldName,
        getTransferIndexFieldValue(asset, fieldName),
      );
      continue;
    }

    if (fieldName === 'coOwners' && isCoOwnersMap(fieldConfig)) {
      const coOwners = (asset.coOwners ?? []).filter(
        (coOwner: CoOwner) =>
          isNotEmptyValue(coOwner.coOwnerDocument) || isNotEmptyValue(coOwner.coOwnerShare),
      );
      for (const [coOwnerFieldName, coOwnerFieldGroup] of Object.entries(fieldConfig)) {
        for (const coOwner of coOwners) {
          updateGroupStats(
            groupStats,
            coOwnerFieldGroup,
            coOwnerFieldName,
            coOwner[coOwnerFieldName as keyof CoOwner],
          );
        }
      }
    }
  }

  const result = availableGroups.reduce((acc, group) => {
    const { filled, total } = groupStats[group];
    if (total === 0) {
      return acc;
    }

    return acc + (groupsWeight[group] * filled) / total;
  }, 0);

  return result;
};

const isNotEmptyValue = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'boolean') {
    return true;
  }

  if (typeof value === 'number') {
    return true;
  }

  if (typeof value === 'string') {
    return value !== '';
  }

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'object') {
    return Object.keys(value).length > 0;
  }

  return false;
};
